/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAccount, useMsal } from '@azure/msal-react'
import { loginRequest, msalInstance } from '../Provider/MsalAuthProvider'
import { getActiveAuthType } from './authUtils'
import { useOktaAuth } from './useOktaAuth'

// Utility to derive current authentication status for MSAL without relying on
// hooks that only work inside the React subtree rendered by <MsalProvider />.
const getMsalIsAuthenticated = (): boolean => {
  try {
    return msalInstance.getAllAccounts().length > 0
  } catch {
    return false
  }
}

export type AuthToken = {
  claims?: {
    gpId: string
    email: string
    firstName: string
    lastName: string
  }
  id?: string
}
export const defaultAuthToken = {
  claims: {
    gpId: '',
    email: '',
    firstName: '',
    lastName: '',
  },
  id: '',
}
export type Auth = {
  logout: () => Promise<void>
  isAuthenticated: boolean
  loginRedirect: () => void
  inProgress?: string
  instance?: any
  authState?: any
  authStateNew?: any
}

export function useAuth(): Auth {
  const activeAuthType = getActiveAuthType()

  const oktaAuth = useOktaAuth()
  const { accounts } = useMsal()
  const account = useAccount(accounts[0] || {})

  // For MSAL we expose real helpers by delegating to the shared
  // PublicClientApplication instance created in MsalAuthProvider.
  const msalAuth: Auth = {
    // Clear MSAL cache and redirect to the Azure AD sign-out endpoint.
    logout: async () => {
      try {
        // Clear NPS survey timestamps on logout
        localStorage.removeItem('nps_login_timestamp')
        localStorage.removeItem('nps_dismiss_timestamp')

        await msalInstance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin,
        })
      } catch (error) {
        /* eslint-disable no-console */
        console.error('[useAuth] MSAL logout failed:', error)
        /* eslint-enable no-console */
      }
    },
    // Derive current authentication state synchronously.
    isAuthenticated: getMsalIsAuthenticated(),
    // Trigger an interactive login if needed.
    loginRedirect: () => {
      msalInstance.loginRedirect(loginRequest).catch((error) => {
        /* eslint-disable no-console */
        console.error('[useAuth] MSAL loginRedirect failed:', error)
        /* eslint-enable no-console */
      })
    },
    // These two are mostly for parity with the Okta object; they aren’t used
    // in the (now default) MSAL flow but exposing them keeps the Auth shape
    // stable across providers.
    inProgress: 'none',
    instance: msalInstance,
    authState: {
      idToken: {
        claims: {
          name: account?.name,
          email: account?.username,
        },
      },
    },
    authStateNew: {
      idToken: {
        claims: {
          name: account?.name || account?.idTokenClaims?.name,
          email: account?.username || account?.idTokenClaims?.preferred_username,
          gpId: account?.idTokenClaims?.gpId,
        },
      },
    },
  }

  const authTypeDict = {
    okta: oktaAuth,
    msal: msalAuth,
  }

  return authTypeDict[activeAuthType]
}
